import React from 'react';
import { CartFeaturesSettings, Feature } from '@cart-js/core';
import { useSelector } from 'react-redux';
import { selectBehaviorFromBootstrap, selectExperimentsFromBootstrap, selectIsWebViewFromBootstrap } from 'store/slice/bootstrap';
import { getCartFeaturesBehavior, selectIsRebrandCartToSaved } from 'core/utils/cart';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';

export interface CartIngressConfig {
    setCartFeature: (features: Partial<CartFeaturesSettings>) => void;
}

const selectDronificationFeatures = (experiments: Partial<Record<ExperimentId, boolean>>) => {
    return {
        isMigrateSimpleComponentsToDrone: isB(ExperimentId.MigrateSimpleComponentsToDroneCartJS, experiments),
        isMigrateButtonToDrone: isB(ExperimentId.MigrateButtonToDroneCartJS, experiments),
        isMigrateTypographyToDrone: isB(ExperimentId.MigrateTypographyToDroneCartJS, experiments),
        isMigrateBoxToDronePt1: isB(ExperimentId.MigrateBoxToDroneCartJSPt1, experiments),
        isMigrateAlertAndDottedProgressToDrone: isB(ExperimentId.MigrateAlertAndDottedProgressToDroneCartJS, experiments),
    };
};

export const useCartFeature = ({ setCartFeature }: CartIngressConfig) => {
    const isWebView = useSelector(selectIsWebViewFromBootstrap);
    const behavior = useSelector(selectBehaviorFromBootstrap);
    const cartFeatures = getCartFeaturesBehavior(behavior);
    const experiments = useSelector(selectExperimentsFromBootstrap);

    React.useEffect(() => {
        const dronification = selectDronificationFeatures(experiments);

        const features: Partial<CartFeaturesSettings> = {
            ShoppingCartIconStyle: cartFeatures.shoppingCartIconStyle,
            TaxAndFeeLabelInCart: cartFeatures.taxAndFeeLabelInCart,
            [Feature.EnableTotalPriceInSlider]: cartFeatures.enableTotalPriceInSlider,
            [Feature.RemoveWLHardcodings]: isB(ExperimentId.REMOVE_CART_WL_HARDCODINGS, experiments),
            [Feature.DisplayCartPriceInPoints]: cartFeatures.displayCartPriceInPoints,
            [Feature.FlightOriginRestrictionHandler]: cartFeatures.activitiesOriginRestriction,
            [Feature.ChangeCartToSaved]: selectIsRebrandCartToSaved(behavior, isWebView), // NOTE: Enabling ChangeCartToSaved will enable the features listed here: https://gitlab.agodadev.io/full-stack/cart/cart-js/-/blob/159c57146dc164e190245db7562939f540417c8b/packages/core/src/store/sharedState/features/constants/feature.enum.ts#L151-169

            [Feature.MigrateKiteToDrone]: dronification.isMigrateSimpleComponentsToDrone,
            [Feature.MigrateErrorContentToDrone]: dronification.isMigrateSimpleComponentsToDrone,
            [Feature.MigrateSimpleComponentsToDrone]: dronification.isMigrateSimpleComponentsToDrone,
            [Feature.MigrateButtonToDrone]: dronification.isMigrateButtonToDrone,
            [Feature.MigrateTypographyToDrone]: dronification.isMigrateTypographyToDrone,
            [Feature.MigrateBoxToDronePt1]: dronification.isMigrateBoxToDronePt1,
            [Feature.MigrateKiteAlertAndDottedProgress]: dronification.isMigrateAlertAndDottedProgressToDrone,
            [Feature.EnableDroneCartSlider]: dronification.isMigrateSimpleComponentsToDrone,
        };
        setCartFeature(features);
    }, [cartFeatures, experiments, behavior, isWebView, setCartFeature]);

    return null;
};
